import React, { useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { Col, Row } from "react-bootstrap";
import nextImage from "../../Assets/Images/nextImage.svg";
import prevImage from "../../Assets/Images/prevImage.svg";
import logo from "../../Assets/Images/watermark logo-01.svg";
import "../../Assets/Styles/pages/header.scss";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import helper_functions from "../../Config/helper_functions";
import { message } from "antd";
import {
  setCreate,
  setTemplateReady,
} from "../../Redux/DownloadSlice/downloadSlice";
import {
  setBatchUrlImage,
  setBatchWatermarkSnapshot,
  setTemplateForImage,
  skipBatchUrlImage,
  skipBatchUrlImageOne,
} from "../../Redux/MainImage/imageSlice";
import { setTemplateBatch } from "../../Redux/Templates/templateSlice";
import {
  setCreate_Template,
  setEdit_Template,
  setOnline,
  setTemplate_Changed,
  setTemplate_Edit,
  setTemplateAuthoringAspectPreset,
} from "../../Redux/UserSlice/userSlice";
import { buildCreateTemplateTileRootFields } from "../../Utils/createTemplateTilePayload";
import { getScaledFontSizeWithRespectToTargetScale } from "../Canvas/canvasUtils";
import {
  setActiveId,
  setWaterMarksLoader,
} from "../../Redux/WaterMarks/watermarks";
import { CreateTemplate } from "../../actions/Template/Template";
import DownloadImage from "../DownloadImage/downloadImage";
import NoInternetModal from "../NoInternetModal/noInternetModal";
import ProceedTemplate from "../ProceedTemplate/proceedTemplate";
import Profile from "../Profile/profile";
import SaveModal from "../SaveModal/saveModal";
import SettingsModal from "../SettingsModal/settingsModal";
import SignIn from "../SignIn/signIn";
import ButtonRounded from "../Signature/button";
import TemplateProgress from "../TemplateProgress/templateProgress";
import TemplateResponse from "../TemplateResponse/templateResponse";
import HeaderMobileView from "./HeaderMobileView";
import NewNameModal from "./NewNameModal";
import {
  canSaveSquareTemplate,
  getNextTemplateName,
  squareThumbnailPreview,
} from "./headerUtils";
import type { RootState } from "../../Redux/store";

interface HeaderProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (value: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({ isMenuOpen, setIsMenuOpen }) => {
  const [open, setOpen] = useState(false);
  const [openSignIn, setOpenSignIn] = useState(false);
  const [openProceedTempalte, setOpenProceedTempalte] = useState(false);
  const [openProgress, setOpenProgress] = useState(false);
  const [batchindex, setBatchindex] = useState(null);
  const [loader, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [save, setSave] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [openTemplate, setOpenTemplate] = useState(false);
  const [respTemplate, setResponse] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [newTempName, setNewTempname] = useState("");
  const [newNameModal, setNewNameModal] = useState(false);
  // Prevent duplicate template creation when downloadState.Image updates multiple times
  // while templateReady remains true (e.g. canvas capture runs more than once).
  const creatingTemplateRef = useRef(false);

  const headerRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const history = useHistory();

  const file = useSelector((state: RootState) => state.mainImage);
  const mainImageState = useSelector((state: RootState) => state.mainImage);
  const waterMarksState = useSelector((state: RootState) => state.watermarks);
  const downloadState = useSelector((state: RootState) => state.downloadSlice);
  const userSliceState = useSelector((state: RootState) => state.userSlice);
  // dispatch(setLoader(false))

  const ensureSquareTemplateSave = () => {
    // Template encryption uses the editor canvas (and remapped 1:1 frame for authoring),
    // not raw upload pixels. Allow save whenever the canvas is square — e.g. normal
    // single-image watermarking — even if the source file is not 1:1.
    if (userSliceState.create_Template || userSliceState.edit_Template) {
      return true;
    }
    const cw = Number(userSliceState.canvasSize?.width ?? 0);
    const ch = Number(userSliceState.canvasSize?.height ?? 0);
    const canvasIsSquare =
      cw > 0 && ch > 0 && Math.abs(cw / ch - 1) <= 0.02;
    if (canvasIsSquare) {
      return true;
    }
    const isSquare = canSaveSquareTemplate(
      downloadState.mainImageWidth,
      downloadState.mainImageHeight
    );
    if (!isSquare) {
      message.warning(
        "Template save supports only 1:1 images. Please use a square image."
      );
    }
    return isSquare;
  };

  /**
   * Before flipping `templateReady`, force the canvas back to a 1:1 preset so the save
   * effect (`dataWrapperEncrypt` + `flexible_frame` math) reads a genuinely square
   * `canvasSize` and watermarks are re-aligned to that square - identical to the legacy
   * flow when users had to upload a square image. Does nothing when the preset is
   * already 1:1 or when we're not authoring a template.
   */
  const remapToSquareAndSave = async () => {
    const isAuthoring =
      userSliceState.create_Template || userSliceState.edit_Template;
    const currentPreset = userSliceState.templateAuthoringAspectPreset;
    if (isAuthoring && currentPreset !== "1:1") {
      dispatch(setTemplateAuthoringAspectPreset("1:1"));
      // Two rAFs + a short buffer cover: (1) canvas resize effect dispatching
      // setCanvasSize, (2) useEffect recomputing canvasImageSize (contain fit),
      // (3) watermark components re-running alignment and writing new %-coords
      // back to Redux, (4) updateWatermarks pushing fresh pixel coords into
      // local state. 350ms is conservative but keeps the UI responsive.
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setTimeout(resolve, 350));
        });
      });
    }
    dispatch(setCreate(true));
    dispatch(setTemplateReady(true));
  };

  const saveAsNew = () => {
    if (!ensureSquareTemplateSave()) return;
    if (Object.keys(userSliceState.user_Auth).length) {
      if (waterMarksState.items.length) {
        // Entering template-save mode turns on `create_Template`, which otherwise sizes
        // the canvas from `templateAuthoringAspectPreset` (possibly non-square). Force
        // 1:1 first so values stay aligned with the legacy square save path, then
        // `remapToSquareAndSave` can settle layout before encrypt.
        dispatch(setTemplateAuthoringAspectPreset("1:1"));
        dispatch(setCreate_Template(true));
        setNewNameModal(false);
        setOpenTemplate(true);
      } else {
        alert(t("NoWaterMarksTemplateError"));
        console.log("openProgress", openProgress);
      }
    } else {
      setOpenSignIn(true);
    }
  };

  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOnline(true);
      dispatch(setOnline(true));
    };
    const handleOfflineStatus = () => {
      setIsOnline(false);
      dispatch(setOnline(false));
    };

    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOfflineStatus);

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOfflineStatus);
    };
  }, [navigator.onLine]);

  const condition = () => {
    if (location.pathname === "/edit") {
      if (file.batchurls.length > 0) return headerChoice3();
      else return headerChoice2();
    } else if (location.pathname === "/home") return headerChoice();
    else return headerChoice4();
  };
  const handleHome = async () => {
    history.push("/");
  };
  const handlePage = (page: string) => {
    setIsMenuOpen(false);
    history.push(page);
  };

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleCompleteEdit = (all: boolean) => {
    const id = Date.now();
    let index = batchindex;
    if (all) {
      index = -1;
    }
    dispatch(
      setTemplateForImage({
        index: index,
        tempID: String(id),
      })
    );
    const temp = {
      id: String(id),
      watermarks: waterMarksState.items,
    };
    dispatch(setTemplate_Edit(false));
    dispatch(setTemplateBatch(temp));
    setSave(false);
  };
  const handleIndex = (index: number) => {
    const newIndex = batchindex + index;
    // var old_id = userSliceState.current_TemplateID

    if (newIndex >= 0 && newIndex < file.batchurls.length) {
      dispatch(setWaterMarksLoader(true));
      if (typeof batchindex === "number") {
        dispatch(
          setBatchWatermarkSnapshot({
            index: batchindex,
            items: waterMarksState.items,
          })
        );
      }
      dispatch(setBatchUrlImage(batchindex + index));
      setTimeout(() => {
        dispatch(setBatchUrlImage(batchindex + index));
      }, 100);
      setTimeout(() => {
        dispatch(setWaterMarksLoader(false));
      }, 2000);
      // dispatch(setLoader(true))
      // dispatch(GetTemplateDataFromRedux(id, templateSlice, waterMarksState, false))
      dispatch(setTemplate_Changed(true));
    }
  };

  const handleSkip = () => {
    if (file.batchurls.length > 1) {
      if (batchindex !== 0) {
        dispatch(skipBatchUrlImage(batchindex));
      } else {
        dispatch(skipBatchUrlImageOne());
      }
    } else if (file.batchurls.length === 1) {
      history.push("/");
    } else {
      alert("Cannot skip only one image.");
    }
  };

  useEffect(() => {
    setEdit(userSliceState.template_Edit);
  }, [userSliceState.template_Edit]);
  useEffect(() => {
    if (file.batchurls.length > 0) {
      setBatchindex(file.currentIndex);
    }
  }, [file.currentIndex]);

  useEffect(() => {
    if (loader) setOpen(true);
  }, [loader]);

  useEffect(() => {
    const run = async () => {
    // Wait for canvas export to finish (Create=false). If we run while Create is still true,
    // templateReady gets cleared before runExport reads it — export falls back to full stage
    // (base + watermarks) and/or this effect uses a stale composite Image.
    if (
      downloadState.Image &&
      downloadState.templateReady &&
      !downloadState.Create
    ) {
      if (creatingTemplateRef.current) return;
      creatingTemplateRef.current = true;
      try {
      const sourcesArray = [];
      // Match admin payload: include ALL watermarks (no dedupe/filter)
      const watermarks = waterMarksState.items ?? [];
      const orderedArray: Array<Record<string, unknown>> = [];
      const usedServerIds = new Set<string>();
      const tileDraft = userSliceState.createTemplateTilePattern1;
      const tiledActive =
        tileDraft.tiledEnabled &&
        Boolean(tileDraft.tilePatternId) &&
        tileDraft.tilePatternId !== "none";

      for (let i = 0; i < watermarks.length; i++) {
        let tempTemplate: Record<string, unknown> = {};
        let data: Record<string, unknown>;
        let wmImagePixelDims: { w: number; h: number } | null = null;
        // Match admin: keep stable `localId` while editing an existing template.
        // API requires localId to be a valid UUID; editor may use numeric ids (Date.now()).
        const isUuid = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);
        const candidate: string | null =
          typeof watermarks[i]?.localId === "string" && watermarks[i]?.localId
            ? (watermarks[i].localId as string)
            : typeof watermarks[i]?.id === "string" && watermarks[i]?.id
              ? (watermarks[i].id as string)
              : null;
        const waterMarksId = (candidate && typeof candidate === "string" && isUuid(candidate)) ? candidate : uuidv4();
        // Include all watermark types that have media (image, signature, logo, sticker).
        // When updating a template, existing watermarks must be in sourcesArray so their
        // media is re-uploaded; otherwise the backend may drop them.
        // Match admin: include "file" (generic image) and all media types
        const hasMedia =
          watermarks[i].type === "image" ||
          watermarks[i].type === "file" ||
          watermarks[i].type === "signature" ||
          watermarks[i].type === "logo" ||
          watermarks[i].type === "sticker";
        if (hasMedia) {
          const item = { ...watermarks[i] } as Record<string, unknown>;
          let src: string | File | Blob = (item.src as string) ?? (item.src as File) ?? (item.file as File) ?? "";
          // Resolve blob URLs, http(s) URLs, and relative paths to File (Template expects data URL string or File/Blob)
          if (typeof src === "string" && !src.startsWith("data:")) {
            try {
              src = await helper_functions.WebToFile(src);
            } catch (e) {
              console.warn("Failed to fetch image URL for watermark:", e);
            }
          }
          // If it's an SVG file, convert to high-res PNG like we do elsewhere
          if (src instanceof File && helper_functions.isSvgFile(src)) {
            try {
              src = await helper_functions.convertSvgFileToHighResPng(src, 4);
            } catch (e) {
              console.warn("Failed to convert SVG to PNG:", e);
            }
          }
          data = { ...item, id: waterMarksId };
          delete data.src;
          delete data.file;
          // Ensure each image watermark has a unique persisted name (avoid duplicates like "main-design.png").
          const ts = Date.now();
          const uniqueName = `wmImage${ts}${i ? `_${i}` : ""}.png`;
          // Update size to reflect the actual file size if available
          const fileSize = src instanceof File || src instanceof Blob ? src.size : (item.size as number) || 0;
          data = { ...data, name: uniqueName, size: fileSize };
          // Only add to sources when we have uploadable content (existing serverId watermarks
          // without src are kept by backend; new ones without src would fail upload).
          // Match admin: ALWAYS add media watermarks to sources so backend receives uploads.
          // Fallback to mediaUrl when src is lost (e.g. File GC'd) - re-fetch from CDN.
          let effectiveSrc: string | File | Blob = src;
          const canUpload =
            (typeof effectiveSrc === "string" && effectiveSrc.length > 0) ||
            (effectiveSrc instanceof File && effectiveSrc.size > 0) ||
            (effectiveSrc instanceof Blob && effectiveSrc.size > 0);
          if (!canUpload) {
            const mediaUrl = (item.mediaUrl ?? watermarks[i].mediaUrl) as string | undefined;
            if (mediaUrl && typeof mediaUrl === "string" && mediaUrl.startsWith("http")) {
              try {
                effectiveSrc = await helper_functions.WebToFile(mediaUrl);
              } catch (e) {
                console.warn("Failed to fetch mediaUrl for watermark:", e);
              }
            }
          }
          const canUploadFinal =
            (typeof effectiveSrc === "string" && effectiveSrc.length > 0) ||
            (effectiveSrc instanceof File && effectiveSrc.size > 0) ||
            (effectiveSrc instanceof Blob && effectiveSrc.size > 0);
          if (canUploadFinal) {
            sourcesArray.push({
              local_id: waterMarksId,
              name: uniqueName,
              src: effectiveSrc,
            });
            if (tiledActive) {
              wmImagePixelDims = await helper_functions.getPixelSizeFromBlobOrDataUrl(effectiveSrc);
            }
          }
          tempTemplate = {
            ...tempTemplate,
            media: {
              type: watermarks[i].property ?? watermarks[i].type ?? "image",
              name: uniqueName,
              bytes: watermarks[i].size ?? 0,
            },
          };
        } else data = watermarks[i];

        // Align with admin: persisted watermark JSON `id` should be the watermark UUID (localId),
        // not the runtime numeric id used by the editor.
        data = { ...(data || {}), id: waterMarksId } as Record<string, unknown> & { id: string };


        const canvasW = userSliceState.canvasSize?.width ?? 651;
        const canvasH = userSliceState.canvasSize?.height ?? 651;
        const isTemplateAuthoring =
          userSliceState.create_Template || userSliceState.edit_Template;
        const imageSize = isTemplateAuthoring
          ? {
              // Template authoring: we already remapped the canvas to 1:1 before arriving
              // here (see `remapToSquareAndSave`), so treat the square canvas itself as the
              // reference "image". This matches the legacy mandatory-square flow exactly,
              // regardless of the actual uploaded image dimensions or cover-fit bleed.
              width: canvasW,
              height: canvasH,
              offsetX: 0,
              offsetY: 0,
            }
          : userSliceState.imageFrame && userSliceState.imageFrame.width
          ? {
              width: userSliceState.imageFrame.width,
              height: userSliceState.imageFrame.height,
              offsetX: userSliceState.imageFrame.offsetX,
              offsetY: userSliceState.imageFrame.offsetY,
            }
          : {
              // Fallback: previous behaviour (treat canvas as image)
              width: canvasW,
              height: canvasH,
              offsetX: 0,
              offsetY: 0,
            };

        let dataForEncrypt: Record<string, unknown> = data;
        if (tiledActive) {
          const W = Math.max(1, imageSize.width);
          const H = Math.max(1, imageSize.height);
          const sz = Math.max(0.01, Math.min(1, tileDraft.size));
          if (data.type === "text") {
            const fs = getScaledFontSizeWithRespectToTargetScale(
              String(data.src ?? "").trim() || " ",
              String(data.font ?? "Arial"),
              { width: W, height: H },
              sz
            );
            dataForEncrypt = { ...data, containerWidth: Math.max(1, W * sz), containerHight: Math.max(1, fs * 1.35) };
          } else if (data.type === "image" || data.type === "signature") {
            const el = (data.image ?? watermarks[i].image) as HTMLImageElement | undefined;
            let iw = Number(el?.naturalWidth || el?.width) || 0;
            let ih = Number(el?.naturalHeight || el?.height) || 0;
            if ((iw <= 0 || ih <= 0) && wmImagePixelDims) {
              iw = wmImagePixelDims.w;
              ih = wmImagePixelDims.h;
            }
            const tilePx = Math.max(1, W * sz);
            const ok = iw > 0 && ih > 0;
            const s = ok ? tilePx / Math.max(iw, ih) : 1;
            dataForEncrypt = {
              ...data,
              containerWidth: ok ? iw : tilePx,
              containerHight: ok ? ih : tilePx,
              scaleX: s,
              scaleY: s,
            };
          }
        }

        const encrypted = await helper_functions.dataWrapperEncrypt(
          dataForEncrypt,
          i,
          canvasW,
          imageSize
        );
        data = { ...encrypted, id: waterMarksId };

        // Build watermark payload to match Android: type, data, localId, serverId (only for existing).
        // Do NOT spread tempTemplate - text watermarks must never inherit "media" from previous iteration.
        const itemPayload: Record<string, unknown> = {
          type: data.type,
          data: JSON.stringify(data),
          localId: waterMarksId,
        };
        if (hasMedia && tempTemplate?.media) {
          itemPayload.media = tempTemplate.media;
        }
        const sid = watermarks[i].serverId as string | undefined;
        if (
          userSliceState.edit_Template &&
          userSliceState.current_TemplateID &&
          sid &&
          !usedServerIds.has(sid)
        ) {
          usedServerIds.add(sid);
          itemPayload.serverId = sid;
        }

        orderedArray.push(itemPayload);
      }

      // dispatch(setCreate_Template(false));
      // dispatch(setEdit_Template(false));

      const squareLarge = await squareThumbnailPreview(downloadState);
      const img = await helper_functions.resizeFile(
        squareLarge,
        100,
        100,
        "png",
        10
      );
      const size_thumb = helper_functions.dataURLtoFile(img, "image.png").size;
      const size_large = helper_functions.dataURLtoFile(
        squareLarge,
        "image.png"
      ).size;
      let name;
      if (
        userSliceState?.edit_Template &&
        !userSliceState.create_Template &&
        userSliceState.template
      ) {
        const effectiveId =
          typeof userSliceState.current_TemplateID === "string"
            ? userSliceState.current_TemplateID
            : (userSliceState.current_TemplateID as { landcsapeTemp?: string; portraitTemp?: string })?.landcsapeTemp ||
              (userSliceState.current_TemplateID as { landcsapeTemp?: string; portraitTemp?: string })?.portraitTemp;
        const templateList = Array.isArray(userSliceState.template)
          ? userSliceState.template
          : [];
        const matched = templateList.filter(
          (t: { id: string }) => t.id === effectiveId
        );
        name = matched?.[0]?.name ?? getNextTemplateName();
      } else {
        name = newTempName.length > 0 ? newTempName : getNextTemplateName();
      }
      // Match admin: include canvasRatio for backend validation
      const canvasW = userSliceState.canvasSize?.width ?? 651;
      const canvasH = userSliceState.canvasSize?.height ?? 651;
      const canvasRatio =
        Number.isFinite(canvasW) && Number.isFinite(canvasH) && canvasH > 0
          ? canvasW / canvasH
          : 1;
      const templateData = {
        id:
          userSliceState.current_TemplateID &&
          userSliceState.edit_Template &&
          !userSliceState.create_Template
            ? userSliceState.current_TemplateID
            : uuidv4(),
        name: name,
        platform: "web",
        patternType: "normal",
        orientation:
          mainImageState.orientation === "landscape" ? "landScape" : "portrait",
        templateType: "flexible",
        canvasRatio: Number.isFinite(canvasRatio) && canvasRatio > 0 ? canvasRatio : 1,
        media: {
          thumbSizeInBytes: size_thumb,
          originalSizeInBytes: size_large,
        },
        // "thumb_size_in_bytes": size_thumb,
        // "large_size_in_bytes": size_large,
        // "media_name": "media.png"
      };

      const templateDataWithTile = {
        ...templateData,
        ...buildCreateTemplateTileRootFields(
          userSliceState.createTemplateTilePattern1
        ),
      };

      const templatePayload = {
        ...templateDataWithTile,
        watermarks: orderedArray,
        ...(userSliceState.edit_Template && userSliceState.current_TemplateID
          ? { deletedIds: waterMarksState.deletedWaterMarkIds }
          : {}),
      };

      const resp = await dispatch(
        CreateTemplate(
          templatePayload as Parameters<typeof CreateTemplate>[0],
          squareLarge,
          img,
          sourcesArray,
          userSliceState
        )
      ) as unknown as boolean;
      setResponse(Boolean(resp));
      setOpenTemplate(false);
      if (resp) {
        dispatch(setCreate_Template(false));
        if (userSliceState.edit_Template && !userSliceState.create_Template) {
          dispatch(setEdit_Template(false));
        }
      } else {
        message.error(t("TemplateSaveFailed"));
      }
      } finally {
        creatingTemplateRef.current = false;
        dispatch(setTemplateReady(false));
      }
    }
    };
    void run();
  }, [downloadState.templateReady, downloadState.Image, downloadState.Create, dispatch]);

  const handleTemplate = () => {
    if (!ensureSquareTemplateSave()) {
      setOpenTemplate(false);
      return;
    }
    void remapToSquareAndSave();
  };

  const headerChoice = () => {
    return (
      <Row
        ref={headerRef}
        className="header m-0 d-flex bg-white landing_header border-bottom"
      >
        <Col className="d-flex flex-column align-items-start justify-content-center">
          <Row className="d-flex productSansBold text_22 justify-content-center align-items-center pointer">
            <Col className="p-0">
              <img
                onClick={handleHome}
                src={logo}
                alt="logo"
                className="logo"
              />
            </Col>
            <Col onClick={handleHome} className="logo_text mx-1 m-0 p-0">
              {" "}
              {t("lang.ezywatermark")}
            </Col>
          </Row>
        </Col>
        <Col className="d-flex flex-column align-items-end justify-content-center">
          <Row className="d-flex flex-row-reverse text_15 productSansLight text-black justify-content-end align-items-center flex-nowrap">
            <Col className="ms-4 p-0">
              {!Object.keys(userSliceState.user_Auth).length ? (
                <button
                  onClick={() => {
                    setOpenSignIn(true);
                  }}
                  className="button_text  d-flex justify-content-center align-items-center button_text_signin w-100 px-4 py-3"
                >
                  {t("header.SignIn")}
                </button>
              ) : (
                <Profile setOpenSettings={setOpenSettings} anchor={headerRef} />
              )}
            </Col>
            {/* <Col className="ms-4 me-3 p-0">
            <button
              onClick={() => handlePage("/pricing")}
              className='button_text ' >{t("header.UpgradeToPro")}</button>
          </Col> */}
            <Col className="ms-4 me-3 p-0">
              <button onClick={() => handlePage("/")} className="button_text ">
                {t("header.Home")}
              </button>
            </Col>
          </Row>
        </Col>
        <SignIn open={openSignIn} setOpen={setOpenSignIn} />
        <SettingsModal
          open={openSettings}
          setOpen={setOpenSettings}
          handleDone={() => setOpenSettings(false)}
          parent={false}
        />
      </Row>
    );
  };

  const headerChoice2 = () => {
    return (
      <Row
        ref={headerRef}
        onClick={() => {
          dispatch(setActiveId(""));
        }}
        className="header landing_header m-0 d-flex bg-white "
      >
        <Col className="d-flex flex-column align-items-start justify-content-center ">
          <Row className="d-flex productSansBold text_22 justify-content-center align-items-center pointer flex-nowrap">
            <Col onClick={handleHome} className="p-0">
              <img alt="logo" src={logo} className="logo" />
            </Col>
            <Col onClick={handleHome} className="logo_text  mx-1  m-0 p-0">
              {" "}
              {t("lang.ezywatermark")}
            </Col>
          </Row>
        </Col>
        <Col className="d-flex flex-column align-items-end justify-content-center p-0 m-0">
          <Row className="d-flex p-0 m-0 text_15 productSansLight justify-content-center align-items-center flex-nowrap">
            {userSliceState.edit_Template && (
              <Col>
                <button
                  onClick={() => {
                    setNewNameModal(true);
                  }}
                  className="button_text w_edit_button m-0 py-1 bg_templ d-flex justify-content-center align-items-center button_text_signin"
                >
                  Save as New
                </button>
              </Col>
            )}
            {(userSliceState.edit_Template ||
              userSliceState.create_Template) && (
              <Col>
                <button
                  onClick={() => {
                    if (!ensureSquareTemplateSave()) return;
                    if (Object.keys(userSliceState.user_Auth).length) {
                      if (waterMarksState.items.length) {
                        if (userSliceState.create_Template) {
                          setNewNameModal(true);
                        } else {
                          setOpenTemplate(true);
                        }
                        // if (userSliceState.edit_Template) {
                        //   setTimeout(() => {
                        //     window.location.reload();
                        //   }, 10000);
                        // }
                      } else {
                        alert(t("NoWaterMarksTemplateError"));
                      }
                    } else {
                      setOpenSignIn(true);
                    }
                  }}
                  className="button_text w_edit_button m-0 py-1  bg_templ d-flex justify-content-center align-items-center button_text_signin"
                >
                  {t("UpdateTemplate")}
                </button>
              </Col>
            )}
            {!(
              userSliceState.edit_Template || userSliceState.create_Template
            ) ? (
              <>
                {waterMarksState.items.length > 0 && (
                  <Col>
                    <button
                      className="button_text w_edit_button m-0 py-1  bg_proceed d-flex justify-content-center align-items-center button_text_signin"
                      onClick={() => {
                        if (!ensureSquareTemplateSave()) return;
                        if (Object.keys(userSliceState.user_Auth).length) {
                          setNewNameModal(true);
                        } else {
                          setOpenSignIn(true);
                        }
                      }}
                    >
                      {loader ? (
                        <CircularProgress size={24} />
                      ) : (
                        t("header.SaveTemplate")
                      )}
                    </button>
                  </Col>
                )}
                <Col>
                  <button
                    className="button_text w_edit_button m-0 py-1  bg_proceed d-flex justify-content-center align-items-center button_text_signin"
                    onClick={() => {
                      if (waterMarksState.items.length) setLoading(true);
                      else {
                        if (window.confirm(t("NoWaterMarksProceedError")))
                          setLoading(false);
                      }
                    }}
                  >
                    {loader ? (
                      <CircularProgress size={24} />
                    ) : (
                      t("header.Proceed")
                    )}
                  </button>
                </Col>
              </>
            ) : null}

            <Col className="p-0 mr_2vw m-0">
              {!Object.keys(userSliceState.user_Auth).length ? (
                <button
                  onClick={() => {
                    setOpenSignIn(true);
                  }}
                  className="button_text h-100 d-flex justify-content-center align-items-center button_text_signin w-100 px-4 py-1"
                >
                  {t("header.SignIn")}
                </button>
              ) : (
                <Profile setOpenSettings={setOpenSettings} anchor={headerRef} />
              )}
            </Col>
          </Row>
        </Col>
        <DownloadImage
          setLoaderParent={setLoading}
          open={open}
          setOpen={setOpen}
          setOpenProgress={setOpenProgress}
        />
        <NewNameModal
          open={newNameModal}
          setOpen={setNewNameModal}
          name={newTempName}
          setName={setNewTempname}
          onComplete={saveAsNew} // When user saves the name this function will be called
        />
        <ProceedTemplate
          open={openProceedTempalte}
          setOpen={setOpenProceedTempalte}
          setLoaderParent={() => { return; }}
          setOpenProgress={() => { return; }}
        />
        {/* <DownloadImageProgress
          open={openProgress}
          setOpenProgress={setOpenProgress}
          setOpen={setOpen}
        /> */}
        <SignIn open={openSignIn} setOpen={setOpenSignIn} />
        <SettingsModal
          open={openSettings}
          setOpen={setOpenSettings}
          handleDone={() => setOpenSettings(false)}
          parent={false}
        />
        <TemplateProgress
          open={openTemplate}
          handleTemplate={handleTemplate}
        />
        <TemplateResponse open={respTemplate} setOpen={setResponse} />
      </Row>
    );
  };
  const headerChoice3 = () => {
    return (
      <Row
        ref={headerRef}
        onClick={() => {
          dispatch(setActiveId(""));
        }}
        className="header m-0 p-0 d-flex bg-white "
      >
        <Col className="d-flex flex-column align-items-start justify-content-center mx-3 px-2">
          <Row className="d-flex productSansBold text_22 justify-content-center align-items-center pointer flex-nowrap">
            <Col onClick={handleHome}>
              <img alt="logo" src={logo} className="logo" />
            </Col>
            <Col onClick={handleHome} className="logo_text mx-1  m-0 p-0">
              {" "}
              {t("lang.ezywatermark")}
            </Col>
          </Row>
        </Col>
        <Col>
          <Row className="d-flex text_18 productSansMedium justify-content-center align-items-center flex-nowrap h-100">
            {batchindex + 1 <= file.batchurls.length
              ? "Image " + (batchindex + 1) + " of " + file.batchurls.length
              : "Processing Done"}
          </Row>
        </Col>
        <Col className="d-flex flex-column align-items-end justify-content-center  mx-3 px-2">
          <Row className="d-flex text_19 productSansLight justify-content-center align-items-center flex-nowrap">
            {!userSliceState.template_Edit && (
              <>
                <Col className="mx-2 m-0 p-0">
                  <button
                    onClick={() => handleIndex(-1)}
                    className="button_text m-0 bg_batch batchControlsSmall border-0 d-flex justify-content-center align-items-center button_text_signin"
                  >
                    <img
                      alt="previous"
                      src={prevImage}
                      className="batchControlsSmallIcon"
                    />
                  </button>
                </Col>
                <Col className="mx-2 m-0 p-0">
                  <button
                    onClick={() => handleIndex(+1)}
                    className="button_text m-0 bg_batch batchControlsSmall border-0 d-flex justify-content-center align-items-center button_text_signin"
                  >
                    <img
                      alt="next"
                      src={nextImage}
                      className="batchControlsSmallIcon"
                    />
                  </button>
                </Col>
              </>
            )}
            {/* <Col className=" m-0  mx-2 p-0">
							<ButtonRounded
								onclick={handleTemplateClick}
								text={edit ? "Save" : t("Edit")}
								style={"w_edit_button bg_batch my-0 mb-0 p-0"}
								parrentStyle={""}
							/>
						</Col> */}
            {!edit ? (
              <Col className=" m-0 mx-2 p-0">
                <ButtonRounded
                  onclick={handleSkip}
                  text={"Skip"}
                  style={"w_edit_button bg_batch my-0 mb-0 p-0"}
                  parrentStyle={" "}
                />
              </Col>
            ) : null}
            <Col className="mx-2 m-0 p-0">
              <Profile setOpenSettings={setOpenSettings} anchor={headerRef} />
            </Col>
          </Row>
        </Col>
        <SaveModal
          open={save}
          handleCompleteEdit={handleCompleteEdit}
        />
        <SettingsModal
          open={openSettings}
          setOpen={setOpenSettings}
          handleDone={() => setOpenSettings(false)}
          parent={false}
        />
      </Row>
    );
  };
  const headerChoice4 = () => {
    return (
      <Row
        ref={headerRef}
        className="header m-0 d-flex bg-white landing_header border-bottom"
      >
        <Col className="d-flex flex-column align-items-start justify-content-center">
          <Row className="d-flex productSansBold text_22 justify-content-center align-items-center pointer">
            <Col className="p-0">
              <img
                alt="logo"
                onClick={handleHome}
                src={logo}
                className="logo"
              />
            </Col>
            <Col onClick={handleHome} className="logo_text mx-1 m-0 p-0">
              {" "}
              {t("lang.ezywatermark")}
            </Col>
          </Row>
        </Col>
        <Col className="d-flex flex-column align-items-end justify-content-center">
          <Row className="d-flex flex-row-reverse text_15 productSansLight text-black justify-content-end align-items-center flex-nowrap">
            <Col className="ms-4 p-0">
              {!Object.keys(userSliceState.user_Auth).length ? (
                <button
                  onClick={() => {
                    setOpenSignIn(true);
                  }}
                  className="button_text d-flex justify-content-center align-items-center button_text_signin w-100 px-4 py-3"
                >
                  {t("header.SignIn")}
                </button>
              ) : (
                <Profile setOpenSettings={setOpenSettings} anchor={headerRef} />
              )}
            </Col>
            <Col className="ms-4 me-3 p-0">
              <button
                onClick={() =>
                  // (window.location.href = `${process.env.REACT_APP_GHOST_API_URL}`)
                  handlePage("/blogs")
                }
                className={` ${
                  location.pathname === "/blogs" && "highlightedHeaderText"
                }  button_text `}
              >
                {t("Blogs")}
              </button>
            </Col>
            <Col className="ms-4 me-3 p-0">
              <button
                onClick={() => handlePage("/faq")}
                className={` ${
                  location.pathname === "/faq" && "highlightedHeaderText"
                }  button_text `}
              >
                {t("FAQ")}
              </button>
            </Col>
            <Col className="ms-4 me-3 p-0">
              <button
                onClick={() => handlePage("/pricing")}
                className={` ${
                  location.pathname === "/pricing" && "highlightedHeaderText"
                }  button_text `}
              >
                {t("Pricing")}
              </button>
            </Col>
            <Col className="ms-4 me-3 p-0">
              <button
                onClick={() => handlePage("/features")}
                className={` ${
                  location.pathname === "/features" && "highlightedHeaderText"
                }  button_text `}
              >
                {t("Features")}
              </button>
            </Col>
          </Row>
        </Col>

        <SignIn open={openSignIn} setOpen={setOpenSignIn} />
        <SettingsModal
          open={openSettings}
          setOpen={setOpenSettings}
          handleDone={() => setOpenSettings(false)}
          parent={false}
        />
      </Row>
    );
  };
  return (
    <>
      <div className="d-lg-block d-none header">{condition()}</div>
      <div className="d-lg-none header">
        <HeaderMobileView
          handlePage={handlePage}
          setOpenSettings={setOpenSettings}
          setOpenSignIn={setOpenSignIn}
          handleMenuToggle={handleMenuToggle}
          setIsMenuOpen={setIsMenuOpen}
          isMenuOpen={isMenuOpen}
        />
        <NoInternetModal open={!isOnline} />
      </div>
    </>
  );
};

export default Header;
